/* eslint-disable functional/no-expression-statement */
import * as D3Scale from "d3-scale";
import * as D3Selection from "d3-selection";
import { pipe } from "fp";

import * as Color from "~/Color";
import * as Log from "~/Log";
import * as ChartIcon from "~/Log/Chart/Icon";
import { getXScaledValue } from "~/Log/Chart/StatusEvents";
import { Time } from "~/Time";

const violationTypeToYScale = (
  v: Log.Violation.Violation,
  yScale: Log.Chart.YScale
): number => {
  switch (v.container.type) {
    case Log.Violation.Container.Type.cycleViolation():
      return yScale("OFF") ?? 0;
    case Log.Violation.Container.Type.breakViolation():
      return yScale("SB") ?? 0;
    case Log.Violation.Container.Type.driveViolation():
      return yScale("D") ?? 0;
    case Log.Violation.Container.Type.shiftViolation():
      return yScale("ON") ?? 0;
    default:
      return 0;
  }
};

const generateViolationBackground = (
  v: Log.Violation.Violation,
  xScale: D3Scale.ScaleTime<number, number, never>,
  yScale: Log.Chart.YScale,
  timezone: Time.Zone,
  padding = 8
): string =>
  `${getXScaledValue(v.interval.start.dateTime, xScale, timezone)},${
    violationTypeToYScale(v, yScale) -
    ChartIcon.violationSVGData.height / 2 -
    padding
  } ${
    getXScaledValue(v.interval.start.dateTime, xScale, timezone) +
    ChartIcon.violationSVGData.width / 2 +
    padding
  },${
    violationTypeToYScale(v, yScale) +
    ChartIcon.violationSVGData.height / 2 +
    padding
  } ${
    getXScaledValue(v.interval.start.dateTime, xScale, timezone) -
    ChartIcon.violationSVGData.width / 2 -
    padding
  },${
    violationTypeToYScale(v, yScale) +
    ChartIcon.violationSVGData.height / 2 +
    padding
  }`.replace("  ", " ");

const violationTextOffset = 20;
const textBackgroundWidth = 80;
const textBackgroundHeight = 16;

export const generate = ({
  svg,
  xScale,
  yScale,
  violationEvents,
  topMargin,
  chartTimeStart,
  chartTimeEnd,
  defaultTimezone,
}: Log.Chart.ChartGeneratorProps) => {
  const eventTip = D3Selection.select(".event-tip");
  const tipTitle = D3Selection.select(".tip-title");
  const tipContent = D3Selection.select(".tip-content");

  const formatTiming = (
    timing: Log.Violation.Container.Timing.Timing
  ): string =>
    "dateTime" in timing
      ? Time.DateTime.toHMMSS(timing)
      : `${Time.DateTime.toHMMSS(
          timing.interval.start
        )} on ${Time.DateTime.toDateOnly(
          timing.interval.start
        )} and ending at ${pipe(
          timing,
          Time.Interval.when({
            started: () => "Ongoing",
            stopped: ({ interval: { stop } }) => Time.DateTime.toHMMSS(stop),
          })
        )}`;

  const interval = Time.Interval.Stopped.fromDateTimes(
    chartTimeStart,
    chartTimeEnd
  );

  const events = violationEvents.filter(
    (e) =>
      (Time.Interval.isStopped(e) &&
        pipe(e, Time.Interval.Stopped.contained(interval))) ||
      (!Time.Interval.isStopped(e) &&
        pipe(interval, Time.Interval.Stopped.contains(e.interval.start)))
  );

  const violationPointGroup = svg
    .append("g")
    .attr("class", "violation-point-group")
    .attr("transform", `translate(0,${topMargin})`);

  violationPointGroup
    .selectAll(".violation-background")
    .data(events)
    .enter()
    .append("polygon")
    .attr("class", "violation-background")
    .attr("fill", Color.white())
    .attr("points", (v: Log.Violation.Violation) =>
      generateViolationBackground(v, xScale, yScale, defaultTimezone)
    );

  violationPointGroup
    .selectAll(".violation-text-background")
    .data(events)
    .enter()
    .append("rect")
    .attr("class", "violation-text-background")
    .attr("fill", Color.white())
    .attr("width", textBackgroundWidth)
    .attr("height", textBackgroundHeight)
    .attr("ry", 1)
    .attr("rx", 1)
    .attr(
      "x",
      (v: Log.Violation.Violation) =>
        getXScaledValue(v.interval.start.dateTime, xScale, defaultTimezone) -
        textBackgroundWidth / 2
    )
    .attr(
      "y",
      (v: Log.Violation.Violation) =>
        violationTypeToYScale(v, yScale) +
        violationTextOffset -
        2 -
        textBackgroundHeight / 2
    );

  violationPointGroup
    .selectAll(`.violation-icon`)
    .data(events)
    .enter()
    .append("svg")
    .attr("class", `violation-icon ${ChartIcon.violationSVGData.class}`)
    .attr("viewBox", ChartIcon.violationSVGData.viewBox)
    .attr("width", ChartIcon.violationSVGData.width)
    .attr("height", ChartIcon.violationSVGData.height)
    .attr(
      "x",
      (d: Log.Violation.Violation) =>
        getXScaledValue(d.interval.start.dateTime, xScale, defaultTimezone) -
        ChartIcon.violationSVGData.width / 2
    )
    .attr(
      "y",
      (d: Log.Violation.Violation) =>
        violationTypeToYScale(d, yScale) - ChartIcon.violationSVGData.height / 2
    )
    .append("path")
    .attr("fill", ChartIcon.violationSVGData.fill)
    // TODO: Fix typing
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    .attr("d", ChartIcon.violationSVGData.path)
    .on("mouseover", (event: MouseEvent, v: Log.Violation.Violation) => {
      eventTip.transition().duration(200).style("opacity", 1);
      tipTitle.html(
        `<span><strong>${Time.DateTime.toHMMSS(
          v.interval.start
        )}</strong></span>`
      );
      tipContent.html(
        v.container.type === Log.Violation.Container.Type.breakViolation()
          ? `<div>No required&nbsp;<strong>30 minute break</strong>&nbsp;for the "shift"</div>`
          : `<div><strong class="violation-type">${
              v.container.type
            }</strong> Limit of <strong>${Time.Duration.toEnglishHours(
              v.limit
            )}</strong> exceeded for the&nbsp;<strong>${
              v.container.type === Log.Violation.Container.Type.cycleViolation()
                ? "cycle"
                : "shift"
            }</strong> starting at <strong>${formatTiming(
              v.container.timing
            )}</strong></div>`
      );
      eventTip
        .style("left", `${event.pageX + 12}px`)
        .style("top", `${event.pageY - 8}px`);
    })
    .on("mouseout", () => {
      eventTip.transition().duration(500).style("opacity", 0);
    });

  violationPointGroup
    .selectAll(".violation-type-text")
    .data(events)
    .enter()
    .append("text")
    .attr("class", "violation-type-text")
    .attr("text-anchor", "middle")
    .attr("fill", Color.guardsmanRed())
    .attr("font-size", ".7em")
    .attr("x", (v: Log.Violation.Violation) =>
      getXScaledValue(v.interval.start.dateTime, xScale, defaultTimezone)
    )
    .attr(
      "y",
      (v: Log.Violation.Violation) =>
        violationTypeToYScale(v, yScale) + violationTextOffset
    )
    .text((v: Log.Violation.Violation) => v.container.type);
};

export const update = ({
  svg,
  xScale,
  yScale,
  defaultTimezone,
}: Log.Chart.ChartGeneratorProps) => {
  svg
    .selectAll(".violation-background")
    // TODO: Fix typing
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    .attr("points", (v: Log.Violation.Violation) =>
      generateViolationBackground(v, xScale, yScale, defaultTimezone)
    );

  svg.selectAll(".violation-text-background").attr(
    "x",
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    (v: Log.Violation.Violation) =>
      getXScaledValue(v.interval.start.dateTime, xScale, defaultTimezone) -
      (textBackgroundWidth - 4) / 2
  );

  svg.selectAll(`.violation-icon`).attr(
    "x",
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    (d: Log.Violation.Violation) =>
      getXScaledValue(d.interval.start.dateTime, xScale, defaultTimezone) -
      ChartIcon.violationSVGData.width / 2
  );

  svg
    .selectAll(".violation-type-text")
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    .attr("x", (v: Log.Violation.Violation) =>
      getXScaledValue(v.interval.start.dateTime, xScale, defaultTimezone)
    );
};
